import { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AddIcon,
  ImportIcon,
  DriveFileMoveIcon,
  ArticleOutlinedIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LightbulbOutlinedIcon,
  LocalOfferOutlinedIcon,
  PersonOutlineIcon,
  SettingsIcon,
  UnitIcon,
} from "../../assets/composer-editor-icons";
import Tooltip from "@mui/material/Tooltip";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import useErrorPopup from "../../tools/hooks/showError";
import apiClient from "../../tools/axiosConfig";
import useLongAction from "../shared/long-action/useLongAction";
import { formatImportAnswer, skillsImportAllAnswer } from "../../utils/importExportMessages";
import { importAllSkills, importSkill } from "../../utils/skillImport";
import { patchWithEtag } from "../../tools/conditionalApi";
import {
  buildActionFallback,
  formatReorderError,
  formatUploadError,
  validateFile,
  importLessonStep,
  BUNDLE_IMPORT_ACCEPT,
  BUNDLE_IMPORT_ACCEPTED_TYPES,
  BUNDLE_IMPORT_MAX_MB,
} from "../../utils/fileUploadHelpers";
import {
  parseCoachContent,
  serializeCoachContent,
} from "../admin/lesson/coachContentModel";
import {
  readContentEditorSidebarTree,
  writeContentEditorSidebarTree,
} from "../../utils/contentEditorSidebarTree";
import {
  readContentEditorSidebarCollapsed,
  writeContentEditorSidebarCollapsed,
  COLLAPSED_CONTENT_EDITOR_SIDEBAR_WIDTH_PX,
} from "../../utils/contentEditorSidebarCollapsed";
import {
  readContentEditorSidebarWidth,
  writeContentEditorSidebarWidth,
} from "../../utils/contentEditorSidebarWidth";
import { scrollElementTo } from "../../utils/lessonEditorScroll";
import { resolveLessonType } from "../../utils/lessonTypes";
import {
  buildUnitContentTree,
  getContentLabel,
  getLocalizedEntityTitle,
  getCourseUnits,
  getTopLevelUnits,
  getUnitAncestors,
  getUnitById,
  MAX_UNIT_LEVELS,
  sortByOrder,
} from "../../utils/contentEditorHelpers";
import {
  applyTopLevelUnitOrder,
  findLessonVersionsFromCourse,
  findStepFromCourse,
  findVersionStepsFromCourse,
  mapCourseStepCoachContent,
  mapCourseVersions,
  reorderItems,
} from "../../utils/contentEditorCourseTree";
import {
  CONTENT_EDITOR_COLLAPSE_LESSON_BRANCH_EVENT,
  CONTENT_EDITOR_EXPAND_LESSON_BRANCH_EVENT,
  getEditorPath,
  getLessonEditorPathWithSidebarIntent,
  getLessonAnnotationDetailPath,
  getLessonAnnotationsPath,
  getLessonStepAnnotationDetailPath,
  getLessonStepAnnotationsPath,
  getLessonStepEditorPath,
  getLessonStepContentHash,
  getLessonVersionEditorPath,
  getScenarioStatesPath,
  getScenarioStatePath,
  getScenarioConditionsPath,
  getScenarioConditionContentHash,
  parseScenarioStatesRoute,
  parseScenarioStateRoute,
  parseScenarioConditionsRoute,
  parseLessonAnnotationsRoute,
  parseLessonStepEditorRoute,
  parseLessonStepAnnotationsRoute,
  parseLessonVersionEditorRoute,
  getDetailsTabHighlightFragment,
  getLessonVersionPanelHash,
  notifyLessonEditorHashLinkIntent,
  pathsWithQueryMatch,
} from "../../utils/contentEditorNavigation";
import { applyLessonBranchExpansionFromUrl } from "../../utils/contentEditorSidebarExpansion";
import { ensureLessonBundle } from "../../utils/lessonEditorBundleCache";
import {
  buildCourseSkillsPath,
  isSkillsRoutePath,
} from "../../pages/skills/skillsPaths";
import {
  VERSION_ROW_PAD,
  TREE_DETAILS_CHILD_PAD,
  SUBSTEP_NESTED_PAD,
  CONTENT_ITEM_ROW_PAD,
  PROGRAM_TREE_NODE_PAD,
  UNIT_TREE_NODE_PAD,
  UNIT_TREE_NESTED_PAD_STEP,
} from "./contentEditorSidebarConstants";
import UnitTreeRowTrailingActions from "./UnitTreeRowTrailingActions";
import TreeNode from "./ContentEditorTreeNode";
import NodeActivationBadge from "../shared/activation/NodeActivationBadge";
import InfoToUseChips from "../shared/user-info/InfoToUseChips";
import SkillsSidebarTree from "./SkillsSidebarTree";
import ManageLanguagesDialog from "./ManageLanguagesDialog";
import {
  formatLanguageName,
  getCourseSupportedLanguages,
  normalizeLanguageKey,
} from "../../utils/composerLanguages";
import { setPendingLessonEditorScroll } from "../../utils/lessonEditorPendingScroll";
import { useUnitTreeImport } from "../../hooks/useUnitTreeImport";
import { useCourseUnitImport } from "../../hooks/useCourseUnitImport";
import {
  LLM_QUESTION_SECTION_IDS,
  getSectionFromHash,
} from "./llm-questions/useLlmQuestionHash";
import {
  LLM_QUESTIONS_REFRESH_EVENT,
  LLM_QUESTIONS_UPDATED_EVENT,
} from "./llm-questions/LlmQuestionsList";
import { USER_QUESTIONS_UPDATED_EVENT } from "./user-questions/userQuestionsEvents";
import "../../style/programDetails.css";
import "../../style/llmQuestions.css";

const LLM_QUESTION_DETAIL_ROUTE_RE =
  /^\/composer\/courses\/[^/]+\/llm-questions\/([^/]+)/;
const USER_QUESTION_DETAIL_ROUTE_RE =
  /^\/composer\/courses\/[^/]+\/user-questions\/([^/]+)/;
const LLM_QUESTION_SECTION_LABELS = [
  { key: LLM_QUESTION_SECTION_IDS.info, label: "Info" },
  { key: LLM_QUESTION_SECTION_IDS.tests, label: "Tests" },
  { key: LLM_QUESTION_SECTION_IDS.prompts, label: "Prompts" },
];

/**
 * Course structure sidebar used by lesson editor, unit editor, and the same tree logic as program details.
 */
const ContentEditorSidebar = ({
  course: courseProp,
  unit,
  lesson,
  scenario,
  infoPage,
  lessonVersions,
  activeVersionId,
  scenarioStates = [],
  scenarioConditions = [],
  scenarioActiveStateId = null,
  skillsSidebarSections = [],
  skillsActiveSkillId = null,
  skillsActiveGroupId = null,
  skillsSidebarRefresh = null,
  allPrograms,
  onRefreshCourse,
  onOptimisticUnitsOrder,
  onOptimisticContentOrder,
  displayLanguage,
  /** Switch the composer-wide display language (chips under the program title). */
  onSelectLanguage,
  /** When set (program details page), Program / Details rows update section state instead of navigating. */
  programPageSection,
  /** When set (e.g. Edit lesson page), steps / coach items can be reordered from the tree. */
  lessonSidebarReorder,
  /** Bump when lesson annotations change so the sidebar refetches annotation lists. */
  annotationsSyncVersion = 0,
  /** Optional shell-level prefetch for tree navigation targets. */
  onPrefetchTarget,
}) => {
  const lr = lessonSidebarReorder;
  const [optimisticCourse, setOptimisticCourse] = useState(null);
  const course = optimisticCourse || courseProp;
  // Every in-scope tree node's activation badge is scoped to the owning course's language set.
  const courseSupportedLanguages = course?.supported_languages;
  useEffect(() => { setOptimisticCourse(null); }, [courseProp]);

  /** Prefer live `lessonVersions` from the page; fall back to embedded course tree during URL/state mismatch. */
  const resolvedLessonVersions = useMemo(() => {
    if (Array.isArray(lessonVersions) && lessonVersions.length > 0) {
      return lessonVersions;
    }
    if (lesson?.id != null && course) {
      return findLessonVersionsFromCourse(course, lesson.id);
    }
    return Array.isArray(lessonVersions) ? lessonVersions : [];
  }, [course, lesson?.id, lessonVersions]);

  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const composerCourseIdFromPath = useMemo(() => {
    const match = /^\/composer\/courses\/([^/]+)(?:$|\/(?:skills|llm-questions|user-questions)(?:\/|$))/.exec(
      path
    );
    return match ? decodeURIComponent(match[1]) : null;
  }, [path]);
  const resolvedCourseId = course?.id ?? composerCourseIdFromPath;
  const isSkillsRoute = isSkillsRoutePath(path);
  const isComposerLlmQuestionsRoute = /^\/composer\/courses\/[^/]+\/llm-questions/.test(path);
  const isComposerUserQuestionsRoute = /^\/composer\/courses\/[^/]+\/user-questions/.test(path);
  const isLlmQuestionsRoute =
    path.startsWith("/composer/classification-questions") || isComposerLlmQuestionsRoute;
  const isUserQuestionsRoute =
    path.startsWith("/composer/user-info-questions") || isComposerUserQuestionsRoute;
  const isContentTabActive =
    !isSkillsRoute && !isLlmQuestionsRoute && !isUserQuestionsRoute;
  const isSkillsTabActive = isSkillsRoute;
  const isLlmQuestionsTabActive = isLlmQuestionsRoute;
  const isUserQuestionsTabActive = isUserQuestionsRoute;
  const lessonNavHashFragment = useMemo(
    () => (lesson?.id ? (location.hash || "").replace(/^#/, "") : ""),
    [lesson?.id, location.hash]
  );
  const lessonDetailsTabHighlight = useMemo(
    () => getDetailsTabHighlightFragment(lessonNavHashFragment),
    [lessonNavHashFragment]
  );

  /** Lesson step full-page editor uses `/edit-lesson/:id[/version/:versionId]/step/:stepId`; clicking the lesson row should open the lesson editor, not only scroll. */
  const isOnLessonStepEditorRoute = useMemo(
    () => Boolean(parseLessonStepEditorRoute(location.pathname)),
    [location.pathname]
  );

  /** Parsed step editor route so the sidebar can highlight the step and expand unit → lesson → version → step. */
  const lessonStepEditorRouteMatch = useMemo(
    () => parseLessonStepEditorRoute(location.pathname),
    [location.pathname]
  );

  /** Full-page step editor for the lesson currently shown in the sidebar (only one tree row should be .active). */
  const isLessonStepEditorForCurrentLesson = useMemo(
    () =>
      Boolean(
        lessonStepEditorRouteMatch &&
          lesson?.id &&
          String(lessonStepEditorRouteMatch.lessonId) === String(lesson.id)
      ),
    [lessonStepEditorRouteMatch, lesson?.id]
  );

  const lessonAnnotationsRouteMatch = useMemo(
    () => parseLessonAnnotationsRoute(location.pathname),
    [location.pathname]
  );

  const lessonVersionEditorRouteMatch = useMemo(
    () => parseLessonVersionEditorRoute(location.pathname),
    [location.pathname]
  );

  const lessonStepAnnotationsRouteMatch = useMemo(
    () => parseLessonStepAnnotationsRoute(location.pathname),
    [location.pathname]
  );

  const scenarioStatesRouteMatch = useMemo(
    () => parseScenarioStatesRoute(location.pathname),
    [location.pathname]
  );
  const scenarioStateRouteMatch = useMemo(
    () => parseScenarioStateRoute(location.pathname),
    [location.pathname]
  );
  const scenarioConditionsRouteMatch = useMemo(
    () => parseScenarioConditionsRoute(location.pathname),
    [location.pathname]
  );
  /** Active scenario row should defer to its child (States/Conditions/a state) on sub-routes. */
  const scenarioSubRouteId = useMemo(
    () =>
      scenarioStateRouteMatch?.scenarioId ||
      scenarioStatesRouteMatch?.scenarioId ||
      scenarioConditionsRouteMatch?.scenarioId ||
      null,
    [scenarioStateRouteMatch, scenarioStatesRouteMatch, scenarioConditionsRouteMatch]
  );
  const activeScenarioStateId = scenarioStateRouteMatch?.stateId || scenarioActiveStateId || null;

  const versionQueryForSidebarExpansion = useMemo(
    () => new URLSearchParams(location.search).get("version"),
    [location.search]
  );

  /** Lesson main editor should not look "active" when viewing step editor, step annotations, or lesson annotations. */
  const isLessonBranchSubRouteForCurrentLesson = useMemo(() => {
    if (!lesson?.id) return false;
    if (
      lessonStepEditorRouteMatch &&
      String(lessonStepEditorRouteMatch.lessonId) === String(lesson.id)
    ) {
      return true;
    }
    if (
      lessonVersionEditorRouteMatch &&
      String(lessonVersionEditorRouteMatch.lessonId) === String(lesson.id)
    ) {
      return true;
    }
    if (
      lessonAnnotationsRouteMatch &&
      String(lessonAnnotationsRouteMatch.lessonId) === String(lesson.id)
    ) {
      return true;
    }
    if (
      lessonStepAnnotationsRouteMatch &&
      String(lessonStepAnnotationsRouteMatch.lessonId) === String(lesson.id)
    ) {
      return true;
    }
    return false;
  }, [
    lesson?.id,
    lessonAnnotationsRouteMatch,
    lessonStepAnnotationsRouteMatch,
    lessonStepEditorRouteMatch,
    lessonVersionEditorRouteMatch,
  ]);

  /** Keep the active tree row in view when the URL hash / highlight changes (e.g. scroll-spy or add-step). */
  useLayoutEffect(() => {
    const el = document.querySelector(".pd-sidebar .pd-tree-node.active");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [
    lessonDetailsTabHighlight,
    lessonNavHashFragment,
    location.hash,
    lessonStepEditorRouteMatch,
    lessonAnnotationsRouteMatch,
    lessonStepAnnotationsRouteMatch,
    scenario?.id,
    infoPage?.id,
  ]);

  const [expandedNodes, setExpandedNodes] = useState({});
  const [sidebarWidthPx, setSidebarWidthPx] = useState(() =>
    readContentEditorSidebarWidth(course?.id)
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readContentEditorSidebarCollapsed()
  );
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      writeContentEditorSidebarCollapsed(next);
      return next;
    });
  }, []);

  const asideWidthStyle = useMemo(
    () =>
      sidebarCollapsed
        ? {
            width: COLLAPSED_CONTENT_EDITOR_SIDEBAR_WIDTH_PX,
            minWidth: COLLAPSED_CONTENT_EDITOR_SIDEBAR_WIDTH_PX,
            flexShrink: 0,
          }
        : {
            width: sidebarWidthPx,
            minWidth: sidebarWidthPx,
            flexShrink: 0,
          },
    [sidebarCollapsed, sidebarWidthPx]
  );

  const [showError, ErrorPopupComponent] = useErrorPopup();
  const longAction = useLongAction();
  const [sidebarLessonAnnotationsByLessonId, setSidebarLessonAnnotationsByLessonId] =
    useState({});
  const sidebarLessonAnnotationsRequestKeysRef = useRef({});

  useEffect(() => {
    setSidebarLessonAnnotationsByLessonId({});
    sidebarLessonAnnotationsRequestKeysRef.current = {};
  }, [course?.id]);

  useEffect(() => {
    if (!course) {
      setSidebarLessonAnnotationsByLessonId({});
      return;
    }

    let cancelled = false;
    const lessonIdsToLoad = new Set();
    if (lesson?.id != null) {
      lessonIdsToLoad.add(String(lesson.id));
    }
    getCourseUnits(course).forEach((unitItem) => {
      buildUnitContentTree(unitItem).forEach((item) => {
        if (item?.type === "lesson" && item?.id != null) {
          lessonIdsToLoad.add(String(item.id));
        }
      });
    });
    if (!lessonIdsToLoad.size) return () => {
      cancelled = true;
    };

    lessonIdsToLoad.forEach((lessonId) => {
      const requestKey = `${annotationsSyncVersion}|L:${lessonId}`;
      if (sidebarLessonAnnotationsRequestKeysRef.current[lessonId] === requestKey) {
        return;
      }
      sidebarLessonAnnotationsRequestKeysRef.current[lessonId] = requestKey;

      (async () => {
        try {
          const { data } = await apiClient.get("/backoffice/v1/annotations/", {
            params: { lesson_id: lessonId },
          });
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data?.results)
            ? data.results
            : [];
          if (cancelled) return;
          setSidebarLessonAnnotationsByLessonId((prev) => ({
            ...prev,
            [lessonId]: list.map((annotation) => ({
              ...annotation,
              _stepId: annotation.lesson_step_id ?? annotation._stepId,
            })),
          }));
        } catch {
          if (cancelled) return;
          setSidebarLessonAnnotationsByLessonId((prev) => ({
            ...prev,
            [lessonId]: [],
          }));
        }
      })();
    });

    return () => {
      cancelled = true;
    };
  }, [annotationsSyncVersion, course, lesson?.id]);

  const { unitImportHandlers, importPortal } = useUnitTreeImport({
    showError,
    onRefreshCourse,
  });
  const {
    importingUnit: importingCourseUnit,
    requestImportUnit: requestCourseUnitImport,
    importPortal: courseUnitImportPortal,
  } = useCourseUnitImport({
    courseId: course?.id,
    showError,
    onImported: async () => {
      await onRefreshCourse?.();
    },
    inputId: course?.id ? `content-editor-sidebar-import-unit-${course.id}` : "content-editor-sidebar-import-unit",
  });
  const skillsImportInputRef = useRef(null);
  const skillsImportFolderInputRef = useRef(null);
  const skillsImportAllInputRef = useRef(null);
  const skillsImportAllFolderInputRef = useRef(null);
  const [skillsImporting, setSkillsImporting] = useState(false);
  const skillsImportTargetGroupIdRef = useRef(null);

  const handleSkillsSidebarImportClick = useCallback((groupId = null) => {
    if (skillsImporting) return;
    skillsImportTargetGroupIdRef.current = groupId;
    skillsImportInputRef.current?.click();
  }, [skillsImporting]);

  const handleSkillsSidebarImportFolderClick = useCallback((groupId = null) => {
    if (skillsImporting) return;
    skillsImportTargetGroupIdRef.current = groupId;
    skillsImportFolderInputRef.current?.click();
  }, [skillsImporting]);

  const handleSkillsSidebarImportAllClick = useCallback(() => {
    if (skillsImporting) return;
    skillsImportAllInputRef.current?.click();
  }, [skillsImporting]);

  const handleSkillsSidebarImportAllFolderClick = useCallback(() => {
    if (skillsImporting) return;
    skillsImportAllFolderInputRef.current?.click();
  }, [skillsImporting]);

  // A single-skill import from a .zip/.json file or an unzipped folder's files, into the group chosen
  // on the tree.
  const submitSkillsImport = useCallback(async (source, groupId, event) => {
    const failure = (error) => formatUploadError(error, buildActionFallback("import", "skill"));
    try {
      setSkillsImporting(true);
      const response = await longAction.run({
        label: "Importing the skill…",
        task: async () => {
          const result = await importSkill(apiClient, course.id, source, groupId);
          await skillsSidebarRefresh?.();
          return result;
        },
        success: (result) => formatImportAnswer(result.data, "Skill imported."), failure,
      });
      if (!longAction.enabled && response) showError(formatImportAnswer(response.data, "Skill imported."), "success");
    } catch (error) {
      if (!longAction.enabled) showError(failure(error));
    } finally {
      setSkillsImporting(false);
      if (event?.target) event.target.value = "";
    }
  }, [course?.id, longAction, showError, skillsSidebarRefresh]);

  const handleSkillsSidebarImportFileChange = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    const targetGroupId = skillsImportTargetGroupIdRef.current;
    skillsImportTargetGroupIdRef.current = null;
    if (!file || !course?.id) {
      if (event?.target) event.target.value = "";
      return;
    }

    const validation = validateFile(file, {
      maxSizeMB: BUNDLE_IMPORT_MAX_MB,
      acceptedTypes: BUNDLE_IMPORT_ACCEPTED_TYPES,
    });
    if (!validation.valid) {
      showError(validation.error);
      if (event?.target) event.target.value = "";
      return;
    }

    await submitSkillsImport(file, targetGroupId, event);
  }, [course?.id, showError, submitSkillsImport]);

  // Folder (unzipped bundle) variant of single-skill import.
  const handleSkillsSidebarImportFolderChange = useCallback(async (event) => {
    const files = event?.target?.files;
    const targetGroupId = skillsImportTargetGroupIdRef.current;
    skillsImportTargetGroupIdRef.current = null;
    if (!files || files.length === 0 || !course?.id) {
      if (event?.target) event.target.value = "";
      return;
    }
    await submitSkillsImport(Array.from(files), targetGroupId, event);
  }, [course?.id, submitSkillsImport]);

  // An all-skills import from a .zip/.json file or an unzipped folder's files.
  const submitSkillsImportAll = useCallback(async (source, event) => {
    const failure = (error) => formatUploadError(error, buildActionFallback("import", "skills"));
    try {
      setSkillsImporting(true);
      const response = await longAction.run({
        label: "Importing the skills…",
        task: async () => {
          const result = await importAllSkills(apiClient, course.id, source);
          await skillsSidebarRefresh?.();
          return result;
        }, success: (result) => skillsImportAllAnswer(result.data), failure,
      });
      if (!longAction.enabled && response) {
        const answer = skillsImportAllAnswer(response.data);
        showError(answer.message, answer.status);
      }
    } catch (error) {
      if (!longAction.enabled) showError(failure(error));
    } finally {
      setSkillsImporting(false);
      if (event?.target) event.target.value = "";
    }
  }, [course?.id, longAction, showError, skillsSidebarRefresh]);

  const handleSkillsSidebarImportAllFileChange = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !course?.id) {
      if (event?.target) event.target.value = "";
      return;
    }

    const validation = validateFile(file, {
      maxSizeMB: BUNDLE_IMPORT_MAX_MB,
      acceptedTypes: BUNDLE_IMPORT_ACCEPTED_TYPES,
    });
    if (!validation.valid) {
      showError(validation.error);
      if (event?.target) event.target.value = "";
      return;
    }

    await submitSkillsImportAll(file, event);
  }, [course?.id, showError, submitSkillsImportAll]);

  const handleSkillsSidebarImportAllFolderChange = useCallback(async (event) => {
    const files = event?.target?.files;
    if (!files || files.length === 0 || !course?.id) {
      if (event?.target) event.target.value = "";
      return;
    }
    await submitSkillsImportAll(Array.from(files), event);
  }, [course?.id, submitSkillsImportAll]);

  /* ── LLM questions sidebar data ── */
  const [llmQuestions, setLlmQuestions] = useState([]);
  const [llmQuestionsLoading, setLlmQuestionsLoading] = useState(false);
  const [addingLlmQuestion, setAddingLlmQuestion] = useState(false);
  const [newLlmQuestionName, setNewLlmQuestionName] = useState("");
  const [savingNewLlmQuestion, setSavingNewLlmQuestion] = useState(false);
  const [expandedLlmQuestionIds, setExpandedLlmQuestionIds] = useState(() => new Set());

  /* ── User questions sidebar data ── */
  const [userQuestions, setUserQuestions] = useState([]);
  const [userQuestionsLoading, setUserQuestionsLoading] = useState(false);

  const fetchLlmQuestions = useCallback(async () => {
    if (!course?.id) {
      setLlmQuestions([]);
      return;
    }
    setLlmQuestionsLoading(true);
    try {
      const { data } = await apiClient.get(
        `/backoffice/v1/courses/${encodeURIComponent(course.id)}/classification-questions/`
      );
      setLlmQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setLlmQuestions([]);
    } finally {
      setLlmQuestionsLoading(false);
    }
  }, [course?.id]);

  useEffect(() => {
    if (!isComposerLlmQuestionsRoute) return;
    fetchLlmQuestions();
  }, [isComposerLlmQuestionsRoute, fetchLlmQuestions]);

  useEffect(() => {
    if (!isComposerLlmQuestionsRoute) return undefined;
    const handler = () => fetchLlmQuestions();
    window.addEventListener(LLM_QUESTIONS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(LLM_QUESTIONS_UPDATED_EVENT, handler);
  }, [isComposerLlmQuestionsRoute, fetchLlmQuestions]);

  const activeLlmQuestionId = useMemo(() => {
    if (!isComposerLlmQuestionsRoute) return null;
    const match = LLM_QUESTION_DETAIL_ROUTE_RE.exec(location.pathname);
    return match ? match[1] : null;
  }, [isComposerLlmQuestionsRoute, location.pathname]);

  const activeLlmSection = useMemo(() => {
    if (!activeLlmQuestionId) return null;
    return getSectionFromHash(location.hash);
  }, [activeLlmQuestionId, location.hash]);

  /** Automatically expand the tree node for the question being viewed. */
  useEffect(() => {
    if (!activeLlmQuestionId) return;
    setExpandedLlmQuestionIds((prev) => {
      if (prev.has(String(activeLlmQuestionId))) return prev;
      const next = new Set(prev);
      next.add(String(activeLlmQuestionId));
      return next;
    });
  }, [activeLlmQuestionId]);

  const toggleLlmQuestionExpanded = useCallback((questionId) => {
    const key = String(questionId);
    setExpandedLlmQuestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectLlmQuestion = useCallback(
    (questionId, section) => {
      if (!course?.id) return;
      if (questionId == null) {
        navigate(`/composer/courses/${course.id}/llm-questions`);
        return;
      }
      const basePath = `/composer/courses/${course.id}/llm-questions/${encodeURIComponent(
        questionId
      )}`;
      const nextHash = section ? `#${section}` : "";
      if (location.pathname === basePath) {
        navigate({ pathname: basePath, hash: nextHash }, { replace: true });
      } else {
        navigate(`${basePath}${nextHash}`);
      }
    },
    [course?.id, location.pathname, navigate]
  );

  const saveNewLlmQuestionDisabled = useMemo(() => {
    const name = newLlmQuestionName.trim();
    if (!name) return true;
    if (savingNewLlmQuestion) return true;
    if (llmQuestions.some((q) => q?.name === name)) return true;
    return false;
  }, [newLlmQuestionName, savingNewLlmQuestion, llmQuestions]);

  const saveNewLlmQuestion = useCallback(async () => {
    if (saveNewLlmQuestionDisabled || !course?.id) return;
    const name = newLlmQuestionName.trim();
    setSavingNewLlmQuestion(true);
    try {
      const { data } = await apiClient.post(
        `/backoffice/v1/courses/${encodeURIComponent(course.id)}/classification-questions/`,
        { name, definition: "" }
      );
      setAddingLlmQuestion(false);
      setNewLlmQuestionName("");
      await fetchLlmQuestions();
      try {
        window.dispatchEvent(new CustomEvent(LLM_QUESTIONS_REFRESH_EVENT));
      } catch (_) {
        /* no-op */
      }
      const createdId = data?.id ?? null;
      if (createdId != null) {
        selectLlmQuestion(createdId, LLM_QUESTION_SECTION_IDS.info);
      }
    } catch (err) {
      console.error(err);
      showError(`${err}`);
    } finally {
      setSavingNewLlmQuestion(false);
    }
  }, [
    saveNewLlmQuestionDisabled,
    course?.id,
    newLlmQuestionName,
    fetchLlmQuestions,
    selectLlmQuestion,
    showError,
  ]);

  const fetchUserQuestions = useCallback(async () => {
    if (!course?.id) {
      setUserQuestions([]);
      return;
    }
    setUserQuestionsLoading(true);
    try {
      const { data } = await apiClient.get(
        `/backoffice/v1/courses/${encodeURIComponent(course.id)}/user-info-fields/`
      );
      setUserQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setUserQuestions([]);
    } finally {
      setUserQuestionsLoading(false);
    }
  }, [course?.id]);

  useEffect(() => {
    if (!isComposerUserQuestionsRoute) return;
    fetchUserQuestions();
  }, [isComposerUserQuestionsRoute, fetchUserQuestions]);

  useEffect(() => {
    if (!isComposerUserQuestionsRoute) return undefined;
    const handler = () => fetchUserQuestions();
    window.addEventListener(USER_QUESTIONS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(USER_QUESTIONS_UPDATED_EVENT, handler);
  }, [isComposerUserQuestionsRoute, fetchUserQuestions]);

  const activeUserQuestionId = useMemo(() => {
    if (!isComposerUserQuestionsRoute) return null;
    const match = USER_QUESTION_DETAIL_ROUTE_RE.exec(location.pathname);
    if (!match || match[1] === "new") return null;
    return match[1];
  }, [isComposerUserQuestionsRoute, location.pathname]);

  const selectUserQuestion = useCallback(
    (fieldId) => {
      if (!course?.id) return;
      if (fieldId == null) {
        navigate(`/composer/courses/${course.id}/user-questions`);
        return;
      }
      navigate(`/composer/courses/${course.id}/user-questions/${encodeURIComponent(fieldId)}`);
    },
    [course?.id, navigate]
  );

  const createUserQuestion = useCallback(() => {
    if (!course?.id) return;
    navigate(`/composer/courses/${course.id}/user-questions/new`);
  }, [course?.id, navigate]);

  const prefetchTarget = useCallback(
    (target) => {
      if (!target) return;
      if (onPrefetchTarget) {
        Promise.resolve(onPrefetchTarget(target)).catch((error) => {
          console.error("Sidebar prefetch failed:", error);
        });
        return;
      }
      if (target.kind === "lesson" && target.entityId) {
        Promise.resolve(ensureLessonBundle(target.entityId)).catch((error) => {
          console.error("Sidebar lesson bundle prefetch failed:", error);
        });
      }
    },
    [onPrefetchTarget]
  );

  const lessonVersionStepImportInputRef = useRef(null);
  const lessonVersionStepImportFolderInputRef = useRef(null);
  const lessonVersionStepImportVersionIdRef = useRef(null);
  const [importingLessonVersionStepId, setImportingLessonVersionStepId] = useState(null);

  const requestLessonVersionStepImport = useCallback((versionId) => {
    if (versionId == null) return;
    lessonVersionStepImportVersionIdRef.current = versionId;
    lessonVersionStepImportInputRef.current?.click();
  }, []);

  const requestLessonVersionStepImportFolder = useCallback((versionId) => {
    if (versionId == null) return;
    lessonVersionStepImportVersionIdRef.current = versionId;
    lessonVersionStepImportFolderInputRef.current?.click();
  }, []);

  // A lesson-step import from a .zip/.json file or an unzipped folder's files.
  const submitLessonVersionStepImport = useCallback(
    async (versionId, source, event) => {
      const failure = (error) => formatUploadError(error, buildActionFallback("import", "lesson step"));
      try {
        setImportingLessonVersionStepId(versionId);
        await longAction.run({
          label: "Importing the step…",
          task: async () => {
            const response = await importLessonStep(apiClient, versionId, source);
            await onRefreshCourse?.();
            return response;
          }, success: (response) => formatImportAnswer(response.data, "Step imported."), failure,
        });
      } catch (error) {
        if (!longAction.enabled) showError(failure(error));
      } finally {
        setImportingLessonVersionStepId(null);
        if (event?.target) event.target.value = "";
      }
    }, [longAction, onRefreshCourse, showError]
  );

  const onLessonVersionStepImportChange = useCallback(
    async (e) => {
      const versionId = lessonVersionStepImportVersionIdRef.current;
      lessonVersionStepImportVersionIdRef.current = null;
      const file = e.target.files?.[0];
      if (versionId == null || !file) {
        try {
          if (e?.target) e.target.value = "";
        } catch (_) {}
        return;
      }

      const validation = validateFile(file, {
        maxSizeMB: BUNDLE_IMPORT_MAX_MB,
        acceptedTypes: BUNDLE_IMPORT_ACCEPTED_TYPES,
      });
      if (!validation.valid) {
        showError(validation.error);
        try {
          if (e?.target) e.target.value = "";
        } catch (_) {}
        return;
      }

      await submitLessonVersionStepImport(versionId, file, e);
    },
    [showError, submitLessonVersionStepImport]
  );

  const onLessonVersionStepImportFolderChange = useCallback(
    async (e) => {
      const versionId = lessonVersionStepImportVersionIdRef.current;
      lessonVersionStepImportVersionIdRef.current = null;
      const files = e.target.files;
      if (versionId == null || !files || files.length === 0) {
        try {
          if (e?.target) e.target.value = "";
        } catch (_) {}
        return;
      }
      await submitLessonVersionStepImport(versionId, Array.from(files), e);
    },
    [submitLessonVersionStepImport]
  );

  const previousCourseIdRef = useRef(null);
  const previousLessonIdForSidebarRef = useRef(null);
  const previousScenarioIdForSidebarRef = useRef(null);
  const previousInfoPageIdForSidebarRef = useRef(null);
  const previousUnitIdForSidebarRef = useRef(null);
  const skipPersistForCourseRef = useRef(null);
  const sidebarResizeStartXRef = useRef(0);
  const sidebarResizeStartWidthRef = useRef(520);

  const clampSidebarWidth = useCallback((w) => {
    if (typeof window === "undefined") return w;
    const min = 260;
    const max = Math.max(min + 40, Math.min(1200, window.innerWidth - 360));
    return Math.round(Math.min(max, Math.max(min, w)));
  }, []);

  useEffect(() => {
    setSidebarWidthPx(readContentEditorSidebarWidth(course?.id));
  }, [course?.id]);

  useEffect(() => {
    const onResize = () => {
      setSidebarWidthPx((prev) => clampSidebarWidth(prev));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampSidebarWidth]);

  const onSidebarResizeMouseDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      sidebarResizeStartXRef.current = event.clientX;
      sidebarResizeStartWidthRef.current = sidebarWidthPx;

      const onMove = (ev) => {
        const dx = ev.clientX - sidebarResizeStartXRef.current;
        setSidebarWidthPx(
          clampSidebarWidth(sidebarResizeStartWidthRef.current + dx)
        );
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        setSidebarWidthPx((w) => {
          const next = clampSidebarWidth(w);
          writeContentEditorSidebarWidth(course?.id, next);
          return next;
        });
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [clampSidebarWidth, course?.id, sidebarWidthPx]
  );

  const onSidebarResizeKeyDown = useCallback(
    (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const step = event.shiftKey ? 48 : 16;
      const delta = event.key === "ArrowRight" ? step : -step;
      setSidebarWidthPx((w) => {
        const next = clampSidebarWidth(w + delta);
        writeContentEditorSidebarWidth(course?.id, next);
        return next;
      });
    },
    [clampSidebarWidth, course?.id]
  );

  // The roots of the tree are the units OF THE COURSE. A unit inside one is drawn where its
  // parent's content list references it, so listing it here as well would show it twice.
  const rawUnits = useMemo(() => {
    const topLevel = getTopLevelUnits(course);
    return topLevel.length ? topLevel : unit ? [unit] : [];
  }, [course, unit]);

  const units = useMemo(() => sortByOrder(rawUnits), [rawUnits]);

  const programsList = useMemo(() => {
    const list = Array.isArray(allPrograms)
      ? allPrograms.filter((program) => program?.id != null)
      : [];
    return [...list].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
  }, [allPrograms]);

  const currentCourseId = course?.id;
  const skillsSections = useMemo(
    () => (Array.isArray(skillsSidebarSections) ? skillsSidebarSections : []),
    [skillsSidebarSections]
  );
  const activeSkillsHashGroupId = useMemo(() => {
    const match = /^#skills-group-(.+)$/.exec(location.hash || "");
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }, [location.hash]);

  const sidebarProgramLabel = useMemo(() => {
    const prog = programsList.find((p) => String(p?.id) === String(currentCourseId));
    const entity = prog && course?.id === prog?.id ? course : prog || course;
    if (!entity) return "";
    return getLocalizedEntityTitle(entity, displayLanguage, "") || `Program #${entity?.id || "?"}`;
  }, [programsList, currentCourseId, course, displayLanguage]);

  /* ── Composer display-language selector (chips + manage dialog) ── */
  const [manageLanguagesOpen, setManageLanguagesOpen] = useState(false);
  const sidebarSupportedLanguages = useMemo(
    () => (course ? getCourseSupportedLanguages(course) : []),
    [course]
  );
  const normalizedDisplayLanguage = useMemo(
    () => normalizeLanguageKey(displayLanguage),
    [displayLanguage]
  );
  const handleManageLanguagesSaved = useCallback(() => {
    onRefreshCourse?.();
  }, [onRefreshCourse]);

  const onContentTabClick = useCallback(() => {
    if (!course?.id) return;
    if (!isContentTabActive) {
      navigate(`/composer/courses/${course.id}`);
    }
  }, [course?.id, navigate, isContentTabActive]);

  const onSkillsTabClick = useCallback(() => {
    if (!course?.id) return;
    navigate(buildCourseSkillsPath(course.id));
  }, [course?.id, navigate]);

  const onLlmQuestionsTabClick = useCallback(() => {
    if (resolvedCourseId == null) return;
    navigate(`/composer/courses/${resolvedCourseId}/llm-questions`);
  }, [resolvedCourseId, navigate]);

  const onUserQuestionsTabClick = useCallback(() => {
    if (resolvedCourseId == null) return;
    navigate(`/composer/courses/${resolvedCourseId}/user-questions`);
  }, [resolvedCourseId, navigate]);

  const onProgramRootClick = useCallback(() => {
    if (programPageSection) {
      programPageSection.onSelectSection("details");
    } else if (course?.id != null) {
      navigate(`/composer/courses/${course.id}`);
    } else {
      navigate("/composer/courses/new");
    }
  }, [programPageSection, course?.id, navigate]);

  const isProgramRootActive = useMemo(() => {
    if (programPageSection) {
      return programPageSection.activeSection === "details";
    }
    const p = location.pathname;
    if (p === "/composer/courses/new") return true;
    if (course?.id != null && p === `/composer/courses/${course.id}`) return true;
    return false;
  }, [programPageSection, location.pathname, course?.id]);

  const shouldShowProgramSidebarTree = Boolean(
    course &&
      (programsList.some((p) => String(p?.id) === String(currentCourseId)) ||
        programsList.length === 0)
  );

  const handleDragEnd = useCallback(
    async (result) => {
      if (!result.destination) return;
      if (result.source.droppableId !== result.destination.droppableId) return;
      if (result.source.index === result.destination.index) return;
      const droppableId = result.source.droppableId || "";

      if (lr) {
        const activeLvIds = new Set(
          (resolvedLessonVersions || []).filter((v) => v?.id != null).map((v) => String(v.id))
        );
        const activeStepIds = new Set(
          (resolvedLessonVersions || [])
            .flatMap((v) => v?.lessonSteps || v?.lesson_steps || [])
            .filter((s) => s?.id != null)
            .map((s) => String(s.id))
        );

        const mSteps = /^ce-lesson-steps-(\d+)$/.exec(droppableId);
        if (mSteps && activeLvIds.has(mSteps[1])) {
          lr.reorderSteps?.(Number(mSteps[1]), result.source.index, result.destination.index);
          return;
        }
        const mGoals = /^ce-coach-goals-(\d+)-(\d+)$/.exec(droppableId);
        if (mGoals && activeStepIds.has(mGoals[1])) {
          lr.reorderGoals?.(
            Number(mGoals[1]),
            Number(mGoals[2]),
            result.source.index,
            result.destination.index
          );
          return;
        }
}

      const mStepsFallback = /^ce-lesson-steps-(\d+)$/.exec(droppableId);
      if (mStepsFallback) {
        const versionId = Number(mStepsFallback[1]);
        const steps = findVersionStepsFromCourse(course, versionId);
        if (steps.length === 0) return;
        const { index: srcIdx } = result.source;
        const { index: dstIdx } = result.destination;
        if (srcIdx < 0 || dstIdx < 0 || srcIdx >= steps.length || dstIdx >= steps.length) return;
        const reordered = reorderItems(steps, srcIdx, dstIdx);
        const orderPayload = reordered
          .filter((s) => s?.id != null)
          .map((s, i) => ({ id: s.id, order: i + 1 }));
        setOptimisticCourse(mapCourseVersions(course, versionId, (v) => ({
          ...v,
          lesson_steps: reordered,
        })));
        try {
          await patchWithEtag(`/backoffice/v1/lesson-versions/${versionId}/steps-order/`, orderPayload);
        } catch (error) {
          console.error("Failed to reorder steps:", error);
          showError(formatReorderError(error, "step"));
        }
        onRefreshCourse?.();
        return;
      }

      const mGoalsFallback = /^ce-coach-goals-(\d+)-(\d+)$/.exec(droppableId);
      if (mGoalsFallback) {
        const stepId = Number(mGoalsFallback[1]);
        const blockIdx = Number(mGoalsFallback[2]);
        const step = findStepFromCourse(course, stepId);
        if (!step) return;
        const blocks = parseCoachContent(step.coach_content);
        if (blockIdx < 0 || blockIdx >= blocks.length) return;
        const goals = Array.isArray(blocks[blockIdx]?.goals) ? [...blocks[blockIdx].goals] : [];
        const nonNullGoalIdx = goals.map((g, i) => (g ? i : -1)).filter((i) => i >= 0);
        const { index: from } = result.source;
        const { index: to } = result.destination;
        if (from < 0 || to < 0 || from >= nonNullGoalIdx.length || to >= nonNullGoalIdx.length) return;
        const nextGoals = reorderItems(goals, nonNullGoalIdx[from], nonNullGoalIdx[to]);
        const nextBlocks = blocks.map((b, i) => (i === blockIdx ? { ...b, goals: nextGoals } : b));
        const nextCoach = serializeCoachContent(nextBlocks);
        setOptimisticCourse(mapCourseStepCoachContent(course, stepId, nextCoach));
        try {
          await patchWithEtag(`/backoffice/v1/lesson-steps/${stepId}/`, {
            coach_content: nextCoach,
          });
        } catch (error) {
          console.error("Failed to reorder goals:", error);
          showError(formatReorderError(error, "goal"));
        }
        onRefreshCourse?.();
        return;
      }

      if (droppableId === "course-units") {
        const items = getTopLevelUnits(course);
        const [reorderedUnit] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedUnit);
        const orderPayload = items.map((unitItem, index) => ({
          id: unitItem.id,
          order: index + 1,
        }));
        // The receivers replace the course's whole `units` array, so the optimistic update has to
        // carry the units inside these ones too, or every group would vanish until the next refetch.
        onOptimisticUnitsOrder?.(getCourseUnits(applyTopLevelUnitOrder(course, items)));
        try {
          await patchWithEtag(`/backoffice/v1/courses/${currentCourseId}/units-order/`, orderPayload);
        } catch (error) {
          console.error("Failed to reorder units:", error);
          showError(formatReorderError(error, "unit"));
          onRefreshCourse?.();
        }
        return;
      }

      const match = droppableId.match(/^unit-content-(.+)$/);
      if (!match) return;
      const unitId = parseInt(match[1], 10);
      if (!Number.isFinite(unitId)) return;
      const targetUnit = getCourseUnits(course).find((unitItem) => unitItem.id === unitId);
      if (!targetUnit) return;
      const items = Array.from(buildUnitContentTree(targetUnit));
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
      const orderPayload = items.map((item, index) => ({
        id: item.id,
        type: item.type === "info-page" ? "info_page" : item.type,
        order: index + 1,
      }));
      onOptimisticContentOrder?.(unitId, items);
      try {
        await patchWithEtag(`/backoffice/v1/units/${unitId}/content-order/`, orderPayload);
      } catch (error) {
        console.error("Failed to reorder content:", error);
        showError(formatReorderError(error, "content"));
        onRefreshCourse?.();
      }
    },
    [course, currentCourseId, resolvedLessonVersions, lr, onOptimisticContentOrder, onOptimisticUnitsOrder, onRefreshCourse, showError]
  );

  useEffect(() => {
    if (!course?.id) return;

    const isNewCourse = previousCourseIdRef.current !== course.id;
    if (isNewCourse) {
      previousLessonIdForSidebarRef.current = null;
      previousScenarioIdForSidebarRef.current = null;
      previousInfoPageIdForSidebarRef.current = null;
      previousUnitIdForSidebarRef.current = null;
    }
    const startingExpandedNodes = isNewCourse
      ? readContentEditorSidebarTree(course.id)
      : null;
    const currentUnitKeys = new Set(
      getCourseUnits(course)
        .filter((unitItem) => unitItem?.id != null)
        .map((unitItem) => `unit-${unitItem.id}`)
    );
    const activeUnitKey = unit?.id ? `unit-${unit.id}` : null;
    const activeLessonKey = lesson?.id ? `lesson-${lesson.id}` : null;
    const activeScenarioKey = scenario?.id ? `scenario-${scenario.id}` : null;
    const activeInfoPageKey = infoPage?.id ? `info-page-${infoPage.id}` : null;

    const priorLessonId = previousLessonIdForSidebarRef.current;
    const lessonNavChanged =
      String(priorLessonId) !== String(lesson?.id ?? "");
    previousLessonIdForSidebarRef.current = lesson?.id ?? null;

    const scenarioNavChanged =
      String(previousScenarioIdForSidebarRef.current) !== String(scenario?.id ?? "");
    previousScenarioIdForSidebarRef.current = scenario?.id ?? null;

    const infoPageNavChanged =
      String(previousInfoPageIdForSidebarRef.current) !== String(infoPage?.id ?? "");
    previousInfoPageIdForSidebarRef.current = infoPage?.id ?? null;

    const unitNavChanged =
      String(previousUnitIdForSidebarRef.current) !== String(unit?.id ?? "");
    previousUnitIdForSidebarRef.current = unit?.id ?? null;

    const lessonVersionsLen = resolvedLessonVersions?.length || 0;
    /* Only reset nested version/step expansion when navigating from one saved lesson to another.
       Program / unit views use `lesson: null`; opening a lesson must not collapse persisted or
       user-expanded branches (same bug as a transient empty `lessonVersions` array). */
    const switchedBetweenTwoLessons =
      lessonNavChanged &&
      priorLessonId != null &&
      lesson?.id != null &&
      String(priorLessonId) !== String(lesson.id);

    const shouldCollapseLessonNested =
      Boolean(lesson?.id) && lessonVersionsLen > 0 && switchedBetweenTwoLessons;

    setExpandedNodes((prev) => {
      const baseExpandedNodes = isNewCourse ? startingExpandedNodes : prev;
      const nextExpanded = {
        ...baseExpandedNodes,
      };

      currentUnitKeys.forEach((unitKey) => {
        if (!(unitKey in nextExpanded)) {
          nextExpanded[unitKey] = unitKey === activeUnitKey;
        }
      });

      if (activeLessonKey && (lessonNavChanged || !(activeLessonKey in nextExpanded))) {
        nextExpanded[activeLessonKey] = true;
      }

      if (activeScenarioKey && (scenarioNavChanged || !(activeScenarioKey in nextExpanded))) {
        nextExpanded[activeScenarioKey] = true;
      }

      if (activeInfoPageKey && (infoPageNavChanged || !(activeInfoPageKey in nextExpanded))) {
        nextExpanded[activeInfoPageKey] = true;
      }

      // A unit inside a unit is only on screen when its parent is open, so a deep link to a lesson
      // in a group has to open the whole way down to it.
      const openUnitAndItsAncestors = (unitId) => {
        nextExpanded[`unit-${unitId}`] = true;
        getUnitAncestors(course, unitId).forEach((ancestor) => {
          nextExpanded[`unit-${ancestor.id}`] = true;
        });
      };

      if (activeUnitKey) {
        if (activeLessonKey && lessonNavChanged) {
          openUnitAndItsAncestors(unit.id);
        }
        if (activeScenarioKey && scenarioNavChanged) {
          openUnitAndItsAncestors(unit.id);
        }
        if (activeInfoPageKey && infoPageNavChanged) {
          openUnitAndItsAncestors(unit.id);
        }
        if (!activeLessonKey && !activeScenarioKey && !activeInfoPageKey && unitNavChanged) {
          getUnitAncestors(course, unit.id).forEach((ancestor) => {
            nextExpanded[`unit-${ancestor.id}`] = true;
          });
          nextExpanded[activeUnitKey] = true;
        }
      }

      if (shouldCollapseLessonNested) {
        for (const version of resolvedLessonVersions) {
          if (version?.id != null) {
            nextExpanded[`version-${version.id}`] = false;
          }
          const steps = Array.isArray(version?.lessonSteps)
            ? version.lessonSteps
            : version?.lesson_steps || [];
          for (const step of steps) {
            if (step?.id == null) continue;
            nextExpanded[`step-${step.id}`] = false;
          }
        }
      }

      const hashFragment = lesson?.id ? (location.hash || "").replace(/^#/, "") : "";
      applyLessonBranchExpansionFromUrl(nextExpanded, {
        lesson,
        unit,
        lessonVersions: resolvedLessonVersions,
        hashFragment,
        lessonStepEditorRouteMatch,
        lessonAnnotationsRouteMatch,
        lessonStepAnnotationsRouteMatch,
        lessonVersionEditorRouteMatch,
        versionQueryParam: versionQueryForSidebarExpansion,
      });

      Object.keys(nextExpanded).forEach((key) => {
        if (key.startsWith("unit-") && !currentUnitKeys.has(key)) {
          delete nextExpanded[key];
        }
      });

      return nextExpanded;
    });

    previousCourseIdRef.current = course.id;
    if (isNewCourse) {
      skipPersistForCourseRef.current = course.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lesson/unit only used for keys; ids + hash + route are listed
  }, [
    course?.id,
    lesson?.id,
    scenario?.id,
    infoPage?.id,
    resolvedLessonVersions,
    unit?.id,
    units,
    location.hash,
    location.pathname,
    lessonStepEditorRouteMatch,
    lessonAnnotationsRouteMatch,
    lessonStepAnnotationsRouteMatch,
    lessonVersionEditorRouteMatch,
    versionQueryForSidebarExpansion,
  ]);

  useEffect(() => {
    if (!course?.id) return;
    if (skipPersistForCourseRef.current === course.id) {
      skipPersistForCourseRef.current = null;
      return;
    }
    writeContentEditorSidebarTree(course.id, expandedNodes);
  }, [course?.id, expandedNodes]);

  const toggle = useCallback((key) => {
    setExpandedNodes((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  /** Auto-expand the scenario branch (and the relevant States/Conditions node) on its sub-routes. */
  useEffect(() => {
    const scId =
      scenarioStateRouteMatch?.scenarioId ||
      scenarioStatesRouteMatch?.scenarioId ||
      scenarioConditionsRouteMatch?.scenarioId;
    if (!scId) return;
    setExpandedNodes((prev) => {
      const next = { ...prev, [`scenario-${scId}`]: true };
      if (scenarioStatesRouteMatch || scenarioStateRouteMatch) {
        next[`ce-scenario-states-${scId}`] = true;
      }
      if (scenarioConditionsRouteMatch) {
        next[`ce-scenario-conditions-${scId}`] = true;
      }
      return next;
    });
  }, [scenarioStatesRouteMatch, scenarioStateRouteMatch, scenarioConditionsRouteMatch]);

  useEffect(() => {
    if (!isSkillsRoute) return;
    setExpandedNodes((prev) => {
      let changed = false;
      const next = { ...prev };
      skillsSections.forEach((section) => {
        const groupKey = `skills-group-${section.id}`;
        const groupHasActiveSkill = (section.skills || []).some(
          (skill) => String(skill?.id) === String(skillsActiveSkillId)
        );
        if (!(groupKey in next)) {
          next[groupKey] = true;
          changed = true;
          return;
        }
        if (
          groupHasActiveSkill ||
          String(skillsActiveGroupId || "") === String(section.id) ||
          String(activeSkillsHashGroupId || "") === String(section.id)
        ) {
          if (!next[groupKey]) {
            next[groupKey] = true;
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [
    activeSkillsHashGroupId,
    isSkillsRoute,
    skillsActiveGroupId,
    skillsActiveSkillId,
    skillsSections,
  ]);

  useEffect(() => {
    const onExpandBranch = (event) => {
      const d = event.detail;
      if (!d || String(d.courseId) !== String(course?.id)) return;
      if (lesson?.id && String(d.lessonId) !== String(lesson.id)) return;

      const itemKey = `lesson-${d.lessonId}`;
      const versionKey = `version-${d.versionId}`;
      const stepKey = `step-${d.stepId}`;
      setExpandedNodes((prev) => {
        const next = {
          ...prev,
          [itemKey]: true,
          [versionKey]: true,
          [stepKey]: true,
        };
        if (d.unitId != null) {
          next[`unit-${d.unitId}`] = true;
        }
        return next;
      });
    };
    window.addEventListener(CONTENT_EDITOR_EXPAND_LESSON_BRANCH_EVENT, onExpandBranch);
    return () =>
      window.removeEventListener(CONTENT_EDITOR_EXPAND_LESSON_BRANCH_EVENT, onExpandBranch);
  }, [course?.id, lesson?.id]);

  useEffect(() => {
    const onCollapseBranch = (event) => {
      const d = event.detail;
      if (!d || String(d.courseId) !== String(course?.id)) return;
      if (lesson?.id && String(d.lessonId) !== String(lesson.id)) return;

      const stepKey = `step-${d.stepId}`;
      setExpandedNodes((prev) => {
        const next = { ...prev };
        next[stepKey] = false;
        return next;
      });
    };
    window.addEventListener(CONTENT_EDITOR_COLLAPSE_LESSON_BRANCH_EVENT, onCollapseBranch);
    return () =>
      window.removeEventListener(CONTENT_EDITOR_COLLAPSE_LESSON_BRANCH_EVENT, onCollapseBranch);
  }, [course?.id, lesson?.id]);

  const openLessonStepEditorTarget = useCallback(
    (lessonId, stepId, versionId, hashFragment) => {
      prefetchTarget({ kind: "lesson", entityId: lessonId, stepId });
      const basePath = getLessonStepEditorPath(lessonId, stepId, versionId);
      const normalizedHash = (hashFragment || "").replace(/^#/, "");
      /* `#ce-step-<id>` on the full-page step route is redundant for routing and makes the browser
       * scroll the main pane to `#ce-step-<id>` (the step card sets this id), hiding breadcrumbs/title. */
      const bareStepMatch = normalizedHash ? /^ce-step-(\d+)$/.exec(normalizedHash) : null;
      const isRedundantBareStepHash =
        Boolean(bareStepMatch) && String(bareStepMatch[1]) === String(stepId);
      if (!normalizedHash || isRedundantBareStepHash) {
        navigate(basePath);
        return;
      }

      setPendingLessonEditorScroll(lessonId, normalizedHash);
      notifyLessonEditorHashLinkIntent();

      if (
        pathsWithQueryMatch(basePath, location.pathname, location.search) &&
        (location.hash || "").replace(/^#/, "") === normalizedHash
      ) {
        window.dispatchEvent(
          new CustomEvent("lesson-editor-scroll-to-hash", {
            detail: { hashFragment: normalizedHash, alignTop: true },
          })
        );
        return;
      }

      navigate(`${basePath}#${normalizedHash}`);
    },
    [location.hash, location.pathname, location.search, navigate, prefetchTarget]
  );

  const renderSingleContentItem = useCallback(
    (item, dragHandleProps, unitItemForExpand, unitLevel = 0) => {
      if (item?.id == null) return null;
      const itemKey = `${item.type}-${item.id}`;
      const isActiveLesson = item.type === "lesson" && String(item.id) === String(lesson?.id);
      const isActiveScenario = item.type === "scenario" && String(item.id) === String(scenario?.id);
      const isActiveInfoPage = item.type === "info-page" && String(item.id) === String(infoPage?.id);
      const itemLessonAnnotations =
        item.type === "lesson" && item.id != null
          ? sidebarLessonAnnotationsByLessonId[String(item.id)] || []
          : [];
      const rawVersions = isActiveLesson
        ? resolvedLessonVersions || []
        : item.type === "lesson"
          ? item.versions || []
          : [];
      const versions = rawVersions.filter(
        (v) => v && (v.id != null || isActiveLesson)
      );
      const hasVersions = versions.length > 0;
      const hasLessonAnnotations = itemLessonAnnotations.length > 0;
      const editorPath = getEditorPath(item);
      const lessonCanExpand =
        item.type === "lesson" && item?.id != null;
      /* The active roleplay always exposes States and Conditions branches (data fed via props). */
      const scenarioHasChildren = isActiveScenario;
      const isOnScenarioSubRoute =
        isActiveScenario && String(scenarioSubRouteId || "") === String(item.id);
      const hasChildren = hasVersions || hasLessonAnnotations || scenarioHasChildren;
      const lessonAnnotationsVersionId = isActiveLesson ? activeVersionId : null;
      const itemTarget =
        item.type === "lesson"
          ? { kind: "lesson", entityId: item.id }
          : item.type === "scenario"
            ? { kind: "scenario", entityId: item.id }
            : item.type === "info-page"
              ? { kind: "infoPage", entityId: item.id }
              : null;

      return (
        <div key={itemKey}>
          <TreeNode
            label={getContentLabel(item, displayLanguage)}
            activationBadge={
              <NodeActivationBadge
                activeByLanguage={item.active_by_language}
                supportedLanguages={courseSupportedLanguages}
              />
            }
            customPaddingLeft={CONTENT_ITEM_ROW_PAD + unitLevel * UNIT_TREE_NESTED_PAD_STEP}
            expanded={expandedNodes[itemKey]}
            onToggle={() => toggle(itemKey)}
            draggable={Boolean(dragHandleProps)}
            showLessonIcon={item.type === "lesson"}
            lessonType={item.type === "lesson" ? resolveLessonType(item) : undefined}
            showRoleplayIcon={item.type === "scenario"}
            customIcon={item.type === "info-page" ? <ArticleOutlinedIcon fontSize="small" /> : null}
            hasChildren={hasChildren}
            active={
              (isActiveScenario && !isOnScenarioSubRoute) ||
              isActiveInfoPage ||
              (
                isActiveLesson &&
                !lessonDetailsTabHighlight &&
                !isLessonBranchSubRouteForCurrentLesson
              )
            }
            onMouseEnter={itemTarget ? () => prefetchTarget(itemTarget) : undefined}
            onFocus={itemTarget ? () => prefetchTarget(itemTarget) : undefined}
            onClick={editorPath ? () => {
              if (itemTarget) prefetchTarget(itemTarget);
              if (isActiveLesson) {
                if (isOnLessonStepEditorRoute || isLessonBranchSubRouteForCurrentLesson) {
                  navigate(editorPath);
                  return;
                }
                const scrollRoot = document.querySelector(".pd-content-scroll");
                scrollElementTo(scrollRoot, { top: 0, behavior: "smooth" });
              } else if (isActiveScenario) {
                if (isOnScenarioSubRoute) {
                  navigate(editorPath);
                  return;
                }
                const scrollRoot = document.querySelector(".pd-content-scroll");
                scrollElementTo(scrollRoot, { top: 0, behavior: "smooth" });
              } else if (isActiveInfoPage) {
                const scrollRoot = document.querySelector(".pd-content-scroll");
                scrollElementTo(scrollRoot, { top: 0, behavior: "smooth" });
              } else {
                navigate(editorPath);
              }
            } : undefined}
            dragHandleProps={dragHandleProps}
            trailingActions={
              lessonCanExpand ? (
                <Tooltip title="Add version" placement="top" arrow enterDelay={400} disableInteractive>
                  <button
                    type="button"
                    className="pd-tree-node-action"
                    aria-label="Add version"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(getLessonEditorPathWithSidebarIntent(item.id, { addVersion: true }));
                    }}
                  >
                    <AddIcon />
                  </button>
                </Tooltip>
              ) : undefined
            }
          />
          {expandedNodes[itemKey] && hasChildren && (
            <div className="pd-tree-children">
              {hasLessonAnnotations && (
                <div>
                  <TreeNode
                    label="Annotations"
                    customPaddingLeft={VERSION_ROW_PAD}
                    expanded={expandedNodes[`ce-ann-lesson-${item.id}`]}
                    onToggle={() => toggle(`ce-ann-lesson-${item.id}`)}
                    hasChildren={hasLessonAnnotations}
                    active={Boolean(
                      lessonAnnotationsRouteMatch &&
                        String(lessonAnnotationsRouteMatch.lessonId) === String(item.id) &&
                        !lessonAnnotationsRouteMatch.annotationId &&
                        !lessonStepAnnotationsRouteMatch
                    )}
                    onClick={() =>
                      navigate(getLessonAnnotationsPath(item.id, lessonAnnotationsVersionId))
                    }
                  />
                  {expandedNodes[`ce-ann-lesson-${item.id}`] && hasLessonAnnotations && (
                    <div className="pd-tree-children">
                      {itemLessonAnnotations.map((ann) => (
                        <TreeNode
                          key={`ce-ann-${ann.id}`}
                          label={ann.title?.trim() || `Annotation ${ann.id}`}
                          customPaddingLeft={SUBSTEP_NESTED_PAD}
                          expanded={false}
                          onToggle={undefined}
                          hasChildren={false}
                          active={Boolean(
                            lessonAnnotationsRouteMatch &&
                              String(lessonAnnotationsRouteMatch.lessonId) === String(item.id) &&
                              lessonAnnotationsRouteMatch.annotationId != null &&
                              String(lessonAnnotationsRouteMatch.annotationId) === String(ann.id) &&
                              !lessonStepAnnotationsRouteMatch
                          )}
                          onClick={() =>
                            navigate(
                              getLessonAnnotationDetailPath(
                                item.id,
                                ann.id,
                                lessonAnnotationsVersionId
                              )
                            )
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              {versions.map((version, vi) => {
                const versionKey = `version-${version.id || vi}`;
                const steps = isActiveLesson
                  ? (Array.isArray(version?.lessonSteps)
                      ? version.lessonSteps
                      : (version?.lesson_steps || []).filter((s) => s?.id != null))
                  : ((version.lesson_steps || []).filter((s) => s?.id != null));
                const hasSteps = steps.length > 0;
                const versionCanExpand =
                  item.type === "lesson" && version?.id != null;
                return (
                  <div key={versionKey}>
                    <TreeNode
                      label={`Version: ${getLocalizedEntityTitle(version, displayLanguage, "") || `Version ${vi + 1}`}`}
                      customPaddingLeft={VERSION_ROW_PAD}
                      expanded={expandedNodes[versionKey]}
                      onToggle={() => toggle(versionKey)}
                      hasChildren={hasSteps}
                      active={
                        isActiveLesson &&
                        !isLessonStepEditorForCurrentLesson &&
                        ((lessonVersionEditorRouteMatch &&
                          lesson?.id &&
                          String(lessonVersionEditorRouteMatch.lessonId) === String(lesson.id) &&
                          String(lessonVersionEditorRouteMatch.versionId) === String(version.id)) ||
                          (!lessonVersionEditorRouteMatch &&
                            lessonDetailsTabHighlight === getLessonVersionPanelHash(version.id)))
                      }
                      onMouseEnter={
                        item.type === "lesson" ? () => prefetchTarget({ kind: "lesson", entityId: item.id }) : undefined
                      }
                      onFocus={
                        item.type === "lesson" ? () => prefetchTarget({ kind: "lesson", entityId: item.id }) : undefined
                      }
                      onClick={
                        version?.id != null
                          ? () => {
                              prefetchTarget({ kind: "lesson", entityId: item.id });
                              navigate(getLessonVersionEditorPath(item.id, version.id));
                            }
                          : undefined
                      }
                      trailingActions={
                        versionCanExpand ? (
                          <>
                            <Tooltip
                              title="Import step"
                              placement="top"
                              arrow
                              enterDelay={400}
                              disableInteractive
                            >
                              <span className="pd-tree-node-action-tooltip-wrap">
                                <button
                                  type="button"
                                  className="pd-tree-node-action pd-tree-node-action--import"
                                  aria-label="Import step"
                                  disabled={importingLessonVersionStepId != null}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    requestLessonVersionStepImport(version.id);
                                  }}
                                >
                                  <ImportIcon />
                                </button>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title="Import step from folder"
                              placement="top"
                              arrow
                              enterDelay={400}
                              disableInteractive
                            >
                              <span className="pd-tree-node-action-tooltip-wrap">
                                <button
                                  type="button"
                                  className="pd-tree-node-action pd-tree-node-action--import"
                                  aria-label="Import step from folder"
                                  disabled={importingLessonVersionStepId != null}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    requestLessonVersionStepImportFolder(version.id);
                                  }}
                                >
                                  <DriveFileMoveIcon />
                                </button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Add step" placement="top" arrow enterDelay={400} disableInteractive>
                              <span className="pd-tree-node-action-tooltip-wrap">
                                <button
                                  type="button"
                                  className="pd-tree-node-action"
                                  aria-label="Add step"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      getLessonEditorPathWithSidebarIntent(item.id, {
                                        versionId: version.id,
                                        addStep: true,
                                      })
                                    );
                                  }}
                                >
                                  <AddIcon />
                                </button>
                              </span>
                            </Tooltip>
                          </>
                        ) : undefined
                      }
                    />
                    {expandedNodes[versionKey] && hasSteps && (
                      <div className="pd-tree-children">
                        {(() => {
                          const canReorderSteps = Boolean(
                            version?.id != null && (onRefreshCourse || (lr && isActiveLesson))
                          );
                          const mapLessonStepRow = (step, si, stepDragHandleProps) => {
                            const lessonStepId = step?.id;
                            const stepBranchKey =
                              lessonStepId != null ? `ce-step-branch-${lessonStepId}` : null;
                            const hasStepDetailsLink =
                              item.type === "lesson" &&
                              lessonStepId != null &&
                              version?.id != null;
                            const stepAnnList = itemLessonAnnotations.filter(
                              (a) => String(a._stepId ?? a.lesson_step_id) === String(lessonStepId)
                            );
                            const stepBranchExpanded =
                              stepBranchKey != null && Boolean(expandedNodes[stepBranchKey]);

                            return (
                              <div className="pd-tree-step-cluster">
                                <TreeNode
                                  label={`Step ${si + 1}: ${getLocalizedEntityTitle(step, displayLanguage, "").trim() || "Untitled"}`}
                                  activationBadge={
                                    <NodeActivationBadge
                                      activeByLanguage={step.active_by_language}
                                      supportedLanguages={courseSupportedLanguages}
                                    />
                                  }
                                  customPaddingLeft={VERSION_ROW_PAD}
                                  expanded={stepBranchExpanded}
                                  onToggle={
                                    stepBranchKey && stepAnnList.length > 0
                                      ? () => toggle(stepBranchKey)
                                      : undefined
                                  }
                                  draggable={canReorderSteps && lessonStepId != null}
                                  hasChildren={stepAnnList.length > 0}
                                  active={(() => {
                                    const h = lessonDetailsTabHighlight;
                                    const hashMatchesStepOnly =
                                      h === getLessonStepContentHash(lessonStepId);
                                    const routeMatchesStep =
                                      lessonStepEditorRouteMatch &&
                                      lesson?.id &&
                                      String(lessonStepEditorRouteMatch.lessonId) === String(lesson.id) &&
                                      String(lessonStepEditorRouteMatch.stepId) === String(lessonStepId);
                                    const onStepAnnotationsRoute =
                                      lessonStepAnnotationsRouteMatch &&
                                      lesson?.id &&
                                      String(lessonStepAnnotationsRouteMatch.lessonId) ===
                                        String(lesson.id) &&
                                      String(lessonStepAnnotationsRouteMatch.stepId) ===
                                        String(lessonStepId);
                                    return (
                                      (hashMatchesStepOnly || Boolean(routeMatchesStep)) &&
                                      !onStepAnnotationsRoute
                                    );
                                  })()}
                                  onMouseEnter={
                                    hasStepDetailsLink
                                      ? () =>
                                          prefetchTarget({
                                            kind: "lesson",
                                            entityId: item.id,
                                            stepId: lessonStepId,
                                          })
                                      : undefined
                                  }
                                  onFocus={
                                    hasStepDetailsLink
                                      ? () =>
                                          prefetchTarget({
                                            kind: "lesson",
                                            entityId: item.id,
                                            stepId: lessonStepId,
                                          })
                                      : undefined
                                  }
                                  onClick={
                                    hasStepDetailsLink
                                      ? () =>
                                          openLessonStepEditorTarget(
                                            item.id,
                                            lessonStepId,
                                            version.id,
                                            getLessonStepContentHash(lessonStepId)
                                          )
                                      : undefined
                                  }
                                  dragHandleProps={stepDragHandleProps}
                                />
                                <InfoToUseChips
                                  names={step.info_fields}
                                  compact
                                  style={{ paddingInlineStart: VERSION_ROW_PAD + 24 }}
                                />
                                {lessonStepId != null &&
                                  version?.id != null &&
                                  stepBranchExpanded &&
                                  stepAnnList.length > 0 && (
                                    <div className="pd-tree-children">
                                      <TreeNode
                                        label="Annotations"
                                        customPaddingLeft={TREE_DETAILS_CHILD_PAD}
                                        expanded={expandedNodes[`ce-ann-step-${lessonStepId}`]}
                                        onToggle={() => toggle(`ce-ann-step-${lessonStepId}`)}
                                        hasChildren={stepAnnList.length > 0}
                                        active={Boolean(
                                          lessonStepAnnotationsRouteMatch &&
                                            lesson?.id &&
                                            String(lessonStepAnnotationsRouteMatch.lessonId) ===
                                              String(lesson.id) &&
                                            String(lessonStepAnnotationsRouteMatch.stepId) ===
                                              String(lessonStepId) &&
                                            !lessonStepAnnotationsRouteMatch.annotationId
                                        )}
                                        onClick={() =>
                                          navigate(
                                            getLessonStepAnnotationsPath(
                                              item.id,
                                              lessonStepId,
                                              version.id
                                            )
                                          )
                                        }
                                      />
                                      {expandedNodes[`ce-ann-step-${lessonStepId}`] &&
                                        stepAnnList.length > 0 && (
                                          <div className="pd-tree-children">
                                            {stepAnnList.map((ann) => (
                                              <TreeNode
                                                key={`ce-ann-step-${lessonStepId}-${ann.id}`}
                                                label={ann.title?.trim() || `Annotation ${ann.id}`}
                                                customPaddingLeft={SUBSTEP_NESTED_PAD}
                                                expanded={false}
                                                onToggle={undefined}
                                                hasChildren={false}
                                                active={Boolean(
                                                  lessonStepAnnotationsRouteMatch &&
                                                    lesson?.id &&
                                                    String(lessonStepAnnotationsRouteMatch.lessonId) ===
                                                      String(lesson.id) &&
                                                    String(lessonStepAnnotationsRouteMatch.stepId) ===
                                                      String(lessonStepId) &&
                                                    String(lessonStepAnnotationsRouteMatch.annotationId) ===
                                                      String(ann.id)
                                                )}
                                                onClick={() =>
                                                  navigate(
                                                    getLessonStepAnnotationDetailPath(
                                                      item.id,
                                                      lessonStepId,
                                                      ann.id,
                                                      version.id
                                                    )
                                                  )
                                                }
                                              />
                                            ))}
                                          </div>
                                        )}
                                    </div>
                                  )}
                              </div>
                            );
                          };

                          if (canReorderSteps) {
                            return (
                              <Droppable droppableId={`ce-lesson-steps-${version.id}`} type="LESSON_STEP">
                                {(stepDropProvided) => (
                                  <div
                                    ref={stepDropProvided.innerRef}
                                    {...stepDropProvided.droppableProps}
                                  >
                                    {steps.map((step, si) => {
                                      const sid = step?.id;
                                      const stepKey = `step-${sid || si}`;
                                      if (!sid) {
                                        return (
                                          <div key={stepKey}>
                                            {mapLessonStepRow(step, si, undefined)}
                                          </div>
                                        );
                                      }
                                      return (
                                        <Draggable
                                          key={stepKey}
                                          draggableId={`ce-ls-${version.id}-${sid}`}
                                          index={si}
                                        >
                                          {(stepProv) => (
                                            <div
                                              ref={stepProv.innerRef}
                                              {...stepProv.draggableProps}
                                            >
                                              {mapLessonStepRow(
                                                step,
                                                si,
                                                stepProv.dragHandleProps
                                              )}
                                            </div>
                                          )}
                                        </Draggable>
                                      );
                                    })}
                                    {stepDropProvided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            );
                          }
                          return (
                            <>
                              {steps.map((step, si) => (
                                <div key={`step-${step.id || si}`}>
                                  {mapLessonStepRow(step, si, undefined)}
                                </div>
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {expandedNodes[itemKey] && isActiveScenario && (
            <div className="pd-tree-children">
              <div>
                <TreeNode
                  label="States"
                  customPaddingLeft={VERSION_ROW_PAD}
                  expanded={expandedNodes[`ce-scenario-states-${item.id}`]}
                  onToggle={() => toggle(`ce-scenario-states-${item.id}`)}
                  hasChildren={scenarioStates.length > 0}
                  active={Boolean(
                    scenarioStatesRouteMatch &&
                      String(scenarioStatesRouteMatch.scenarioId) === String(item.id)
                  )}
                  onClick={() => navigate(getScenarioStatesPath(item.id))}
                />
                {expandedNodes[`ce-scenario-states-${item.id}`] && scenarioStates.length > 0 && (
                  <div className="pd-tree-children">
                    {scenarioStates.map((st) => (
                      <TreeNode
                        key={`ce-scenario-state-${st.id}`}
                        label={(st.name || "").trim() || "Untitled state"}
                        activationBadge={
                          <NodeActivationBadge
                            activeByLanguage={st.active_by_language}
                            supportedLanguages={courseSupportedLanguages}
                          />
                        }
                        customPaddingLeft={SUBSTEP_NESTED_PAD}
                        expanded={false}
                        hasChildren={false}
                        active={Boolean(
                          String(activeScenarioStateId || "") === String(st.id) &&
                            (!scenarioStateRouteMatch ||
                              String(scenarioStateRouteMatch.scenarioId) === String(item.id))
                        )}
                        onClick={() => navigate(getScenarioStatePath(item.id, st.id))}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div>
                <TreeNode
                  label="Conditions"
                  customPaddingLeft={VERSION_ROW_PAD}
                  expanded={expandedNodes[`ce-scenario-conditions-${item.id}`]}
                  onToggle={() => toggle(`ce-scenario-conditions-${item.id}`)}
                  hasChildren={scenarioConditions.length > 0}
                  active={Boolean(
                    scenarioConditionsRouteMatch &&
                      String(scenarioConditionsRouteMatch.scenarioId) === String(item.id)
                  )}
                  onClick={() => navigate(getScenarioConditionsPath(item.id))}
                />
                {expandedNodes[`ce-scenario-conditions-${item.id}`] && scenarioConditions.length > 0 && (
                  <div className="pd-tree-children">
                    {scenarioConditions.map((cond) => (
                      <TreeNode
                        key={`ce-scenario-condition-${cond.id}`}
                        label={(cond.name || "").trim() || "Untitled condition"}
                        customPaddingLeft={SUBSTEP_NESTED_PAD}
                        expanded={false}
                        hasChildren={false}
                        active={false}
                        onClick={() =>
                          navigate(
                            `${getScenarioConditionsPath(item.id)}#${getScenarioConditionContentHash(cond.id)}`
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      displayLanguage,
      courseSupportedLanguages,
      expandedNodes,
      scenarioStates,
      scenarioConditions,
      scenarioStatesRouteMatch,
      scenarioStateRouteMatch,
      scenarioConditionsRouteMatch,
      scenarioSubRouteId,
      activeScenarioStateId,
      isOnLessonStepEditorRoute,
      isLessonBranchSubRouteForCurrentLesson,
      isLessonStepEditorForCurrentLesson,
      activeVersionId,
      lesson?.id,
      scenario?.id,
      infoPage?.id,
      resolvedLessonVersions,
      lessonDetailsTabHighlight,
      lessonAnnotationsRouteMatch,
      lessonStepAnnotationsRouteMatch,
      lessonStepEditorRouteMatch,
      lessonVersionEditorRouteMatch,
      sidebarLessonAnnotationsByLessonId,
      lr,
      navigate,
      openLessonStepEditorTarget,
      onRefreshCourse,
      prefetchTarget,
      toggle,
      importingLessonVersionStepId,
      requestLessonVersionStepImport,
      requestLessonVersionStepImportFolder,
    ]
  );

  /**
   * A unit's children can hold a unit, whose children can hold content: the two renderers below call
   * each other. `renderContentItems` is memoized and defined first, so it reaches the unit renderer
   * through a ref that is refreshed on every render.
   */
  const renderUnitNodeRef = useRef(null);

  const renderContentItems = useCallback(
    (contentItems, unitArg, unitLevel = 0) => {
      const validItems = contentItems.filter((item) => item?.id != null);
      const renderChild = (item, dragHandleProps) =>
        item.type === "unit"
          ? renderUnitNodeRef.current?.(item.id, unitLevel + 1, dragHandleProps)
          : renderSingleContentItem(item, dragHandleProps, unitArg, unitLevel);
      if (validItems.length >= 2 && onRefreshCourse) {
        return (
          // The droppable type carries the depth: `@hello-pangea/dnd` only accepts a drag into a list
          // of the same type, and that is what stops an item being dropped out of a group and into
          // the session around it - a move the server refuses, since an item's unit never changes.
          <Droppable droppableId={`unit-content-${unitArg.id}`} type={`UNIT_CONTENT_D${unitLevel}`}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {validItems.map((item, index) => (
                    <Draggable
                      key={`${item.type}-${item.id}`}
                      draggableId={`unit-${unitArg.id}-${item.type}-${item.id}`}
                      index={index}
                    >
                      {(prov) => (
                        <div ref={prov.innerRef} {...prov.draggableProps}>
                          {renderChild(item, prov.dragHandleProps)}
                        </div>
                      )}
                    </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        );
      }
      return validItems.map((item) => renderChild(item, undefined));
    },
    [onRefreshCourse, renderSingleContentItem]
  );

  /** One unit row, at any depth: the course's own session, or a group inside one. */
  const renderUnitNode = useCallback(
    (unitIdOrItem, unitLevel = 0, dragHandleProps = undefined) => {
      const unitItem =
        unitIdOrItem && typeof unitIdOrItem === "object"
          ? unitIdOrItem
          : getUnitById(course, unitIdOrItem);
      if (!unitItem?.id) return null;
      const unitKey = `unit-${unitItem.id}`;
      const isActiveUnit = String(unitItem.id) === String(unit?.id);
      const unitContent = buildUnitContentTree(unitItem);
      const unitHasChildren = unitContent.length > 0;
      const unitLabel = getLocalizedEntityTitle(unitItem, displayLanguage, "") || "Untitled Unit";
      return (
        <div key={unitItem.id}>
          <TreeNode
            label={
              unitLevel === 0
                ? `Unit ${unitItem.order || ""}: ${unitLabel}`
                : unitLabel
            }
            level={unitLevel}
            activationBadge={
              <NodeActivationBadge
                activeByLanguage={unitItem.active_by_language}
                supportedLanguages={courseSupportedLanguages}
              />
            }
            customPaddingLeft={UNIT_TREE_NODE_PAD + unitLevel * UNIT_TREE_NESTED_PAD_STEP}
            expanded={expandedNodes[unitKey]}
            onToggle={() => toggle(unitKey)}
            draggable
            customIcon={<UnitIcon fontSize="small" />}
            hasChildren={unitHasChildren}
            active={isActiveUnit && !lesson?.id && !scenario?.id && !infoPage?.id}
            onMouseEnter={() => prefetchTarget({ kind: "unit", entityId: unitItem.id })}
            onFocus={() => prefetchTarget({ kind: "unit", entityId: unitItem.id })}
            onClick={() => {
              prefetchTarget({ kind: "unit", entityId: unitItem.id });
              navigate(`/composer/courses/edit-unit/${unitItem.id}`);
            }}
            dragHandleProps={dragHandleProps}
            trailingActions={
              <UnitTreeRowTrailingActions
                unitId={unitItem.id}
                courseId={course?.id}
                canHoldUnits={unitLevel + 1 < MAX_UNIT_LEVELS}
                unitImportHandlers={unitImportHandlers}
              />
            }
          />
          {expandedNodes[unitKey] && unitHasChildren && (
            <>
              {renderContentItems(unitContent, unitItem, unitLevel)}
            </>
          )}
        </div>
      );
    },
    [course, courseSupportedLanguages, displayLanguage, expandedNodes, infoPage?.id, lesson?.id, navigate, prefetchTarget, renderContentItems, scenario?.id, toggle, unit, unitImportHandlers]
  );
  renderUnitNodeRef.current = renderUnitNode;

  const renderUnits = useCallback(
    (unitList, unitLevel = 0) => unitList.map((unitItem) => renderUnitNode(unitItem, unitLevel)),
    [renderUnitNode]
  );

  const shouldShowSkillsSidebarTree = Boolean(course && isSkillsRoute);

  const renderSkillsTree = useCallback(
    () => (
      <SkillsSidebarTree
        course={course}
        courseSupportedLanguages={courseSupportedLanguages}
        expandedNodes={expandedNodes}
        toggle={toggle}
        navigate={navigate}
        isSkillsRoute={isSkillsRoute}
        skillsSections={skillsSections}
        skillsActiveGroupId={skillsActiveGroupId}
        skillsActiveSkillId={skillsActiveSkillId}
        activeSkillsHashGroupId={activeSkillsHashGroupId}
        skillsImporting={skillsImporting}
        onImportAll={handleSkillsSidebarImportAllClick}
        onImportAllFolder={handleSkillsSidebarImportAllFolderClick}
        onImportToGroup={handleSkillsSidebarImportClick}
        onImportFolderToGroup={handleSkillsSidebarImportFolderClick}
      />
    ),
    [
      activeSkillsHashGroupId,
      course,
      courseSupportedLanguages,
      expandedNodes,
      handleSkillsSidebarImportAllClick,
      handleSkillsSidebarImportAllFolderClick,
      handleSkillsSidebarImportClick,
      handleSkillsSidebarImportFolderClick,
      isSkillsRoute,
      navigate,
      skillsActiveGroupId,
      skillsActiveSkillId,
      skillsImporting,
      skillsSections,
      toggle,
    ]
  );

  return (
    <aside
      className={`pd-sidebar${sidebarCollapsed ? " pd-sidebar--collapsed" : ""}`}
      role="navigation"
      aria-label="Course content"
      style={asideWidthStyle}
    >
      <div className="pd-sidebar-header">
        <div className="pd-sidebar-header-top">
          <button
            type="button"
            className="pd-sidebar-collapse-toggle"
            onClick={toggleSidebarCollapsed}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </button>
          {!sidebarCollapsed && course ? (
            <p className="pd-sidebar-program-line">
              <span className="pd-sidebar-program-name">{sidebarProgramLabel || ""}</span>
            </p>
          ) : null}
        </div>
        {!sidebarCollapsed && course && sidebarSupportedLanguages.length > 0 ? (
          <div className="pd-sidebar-language-row">
            <div
              className="pd-language-chips pd-sidebar-language-chips"
              role="group"
              aria-label="Display language"
            >
              {sidebarSupportedLanguages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`pd-lang-badge${normalizedDisplayLanguage === lang ? " selected" : ""}`}
                  aria-pressed={normalizedDisplayLanguage === lang}
                  onClick={() => onSelectLanguage?.(lang)}
                  title={`Show content in ${formatLanguageName(lang)}`}
                >
                  {formatLanguageName(lang)}
                </button>
              ))}
            </div>
            <Tooltip title="Manage languages" placement="top" arrow enterDelay={300} disableInteractive>
              <button
                type="button"
                className="pd-sidebar-language-settings"
                onClick={() => setManageLanguagesOpen(true)}
                aria-label="Manage languages"
                aria-haspopup="dialog"
              >
                <SettingsIcon fontSize="small" />
              </button>
            </Tooltip>
          </div>
        ) : null}
        <div className="pd-sidebar-nav" role="tablist" aria-label="Editor sections">
          <Tooltip
            title="Content"
            placement="right"
            arrow
            enterDelay={200}
            disableInteractive
            disableHoverListener={!sidebarCollapsed}
            disableFocusListener={!sidebarCollapsed}
            disableTouchListener={!sidebarCollapsed}
            PopperProps={{
              modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
            }}
          >
            <span className="pd-sidebar-nav-tooltip-anchor">
              <button
                type="button"
                role="tab"
                className={`pd-sidebar-nav-item${isContentTabActive ? " pd-sidebar-nav-item--active" : ""}`}
                aria-selected={isContentTabActive}
                onClick={onContentTabClick}
              >
                <span
                  className={`pd-sidebar-nav-accent${isContentTabActive ? " pd-sidebar-nav-accent--active" : ""}`}
                  aria-hidden="true"
                />
                <span className="pd-sidebar-nav-inner">
                  <span className="pd-sidebar-nav-icon" aria-hidden="true">
                    <ArticleOutlinedIcon fontSize="inherit" />
                  </span>
                  <span className="pd-sidebar-nav-label">Content</span>
                </span>
              </button>
            </span>
          </Tooltip>
          <Tooltip
            title="Skills"
            placement="right"
            arrow
            enterDelay={200}
            disableInteractive
            disableHoverListener={!sidebarCollapsed}
            disableFocusListener={!sidebarCollapsed}
            disableTouchListener={!sidebarCollapsed}
            PopperProps={{
              modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
            }}
          >
            <span className="pd-sidebar-nav-tooltip-anchor">
              <button
                type="button"
                role="tab"
                className={`pd-sidebar-nav-item${isSkillsTabActive ? " pd-sidebar-nav-item--active" : ""}`}
                aria-selected={isSkillsTabActive}
                onClick={onSkillsTabClick}
              >
                <span
                  className={`pd-sidebar-nav-accent${isSkillsTabActive ? " pd-sidebar-nav-accent--active" : ""}`}
                  aria-hidden="true"
                />
                <span className="pd-sidebar-nav-inner">
                  <span className="pd-sidebar-nav-icon" aria-hidden="true">
                    <LightbulbOutlinedIcon fontSize="inherit" />
                  </span>
                  <span className="pd-sidebar-nav-label">Skills</span>
                </span>
              </button>
            </span>
          </Tooltip>
          <Tooltip
            title="LLM Questions"
            placement="right"
            arrow
            enterDelay={200}
            disableInteractive
            disableHoverListener={!sidebarCollapsed}
            disableFocusListener={!sidebarCollapsed}
            disableTouchListener={!sidebarCollapsed}
            PopperProps={{
              modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
            }}
          >
            <span className="pd-sidebar-nav-tooltip-anchor">
              <button
                type="button"
                role="tab"
                className={`pd-sidebar-nav-item${isLlmQuestionsTabActive ? " pd-sidebar-nav-item--active" : ""}`}
                aria-selected={isLlmQuestionsTabActive}
                onClick={onLlmQuestionsTabClick}
              >
                <span
                  className={`pd-sidebar-nav-accent${isLlmQuestionsTabActive ? " pd-sidebar-nav-accent--active" : ""}`}
                  aria-hidden="true"
                />
                <span className="pd-sidebar-nav-inner">
                  <span className="pd-sidebar-nav-icon" aria-hidden="true">
                    <LocalOfferOutlinedIcon fontSize="inherit" />
                  </span>
                  <span className="pd-sidebar-nav-label">LLM Questions</span>
                </span>
              </button>
            </span>
          </Tooltip>
          <Tooltip
            title="User Questions"
            placement="right"
            arrow
            enterDelay={200}
            disableInteractive
            disableHoverListener={!sidebarCollapsed}
            disableFocusListener={!sidebarCollapsed}
            disableTouchListener={!sidebarCollapsed}
            PopperProps={{
              modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
            }}
          >
            <span className="pd-sidebar-nav-tooltip-anchor">
              <button
                type="button"
                role="tab"
                className={`pd-sidebar-nav-item${isUserQuestionsTabActive ? " pd-sidebar-nav-item--active" : ""}`}
                aria-selected={isUserQuestionsTabActive}
                onClick={onUserQuestionsTabClick}
              >
                <span
                  className={`pd-sidebar-nav-accent${isUserQuestionsTabActive ? " pd-sidebar-nav-accent--active" : ""}`}
                  aria-hidden="true"
                />
                <span className="pd-sidebar-nav-inner">
                  <span className="pd-sidebar-nav-icon" aria-hidden="true">
                    <PersonOutlineIcon fontSize="inherit" />
                  </span>
                  <span className="pd-sidebar-nav-label">User Questions</span>
                </span>
              </button>
            </span>
          </Tooltip>
        </div>
        {!sidebarCollapsed ? (
          <div className="pd-sidebar-top-divider" role="presentation" />
        ) : null}
      </div>

      {!sidebarCollapsed && isComposerLlmQuestionsRoute && course?.id ? (
        <div className="pd-sidebar-tree llm-labeling-section" role="tree">
          <div className="pd-sidebar-tree-program-root">
            <TreeNode
              label="All LLM Questions"
              level={0}
              customPaddingLeft={PROGRAM_TREE_NODE_PAD}
              bold
              hasChildren={false}
              active={!activeLlmQuestionId}
              onClick={() => selectLlmQuestion(null)}
              trailingActions={
                <Tooltip title="Add question" placement="top" arrow enterDelay={400} disableInteractive>
                  <button
                    type="button"
                    className="pd-tree-node-action"
                    aria-label="Add question"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddingLlmQuestion(true);
                      setNewLlmQuestionName("");
                    }}
                  >
                    <AddIcon />
                  </button>
                </Tooltip>
              }
            />
          </div>
          {addingLlmQuestion ? (
            <div className="llm-question-add-row">
              <input
                type="text"
                value={newLlmQuestionName}
                onChange={(e) => setNewLlmQuestionName(e.target.value)}
                placeholder="New question name..."
                autoFocus
                disabled={savingNewLlmQuestion}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !saveNewLlmQuestionDisabled) {
                    e.preventDefault();
                    saveNewLlmQuestion();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setAddingLlmQuestion(false);
                    setNewLlmQuestionName("");
                  }
                }}
              />
              <button
                type="button"
                className="pd-tree-node-action"
                aria-label="Save question"
                disabled={saveNewLlmQuestionDisabled}
                onClick={saveNewLlmQuestion}
                title={saveNewLlmQuestionDisabled ? "Enter a unique question name" : "Save"}
                style={{ marginInlineStart: 4 }}
              >
                <AddIcon />
              </button>
            </div>
          ) : null}
          {llmQuestionsLoading && llmQuestions.length === 0 ? (
            <div className="llm-labeling-empty">Loading…</div>
          ) : llmQuestions.length === 0 && !addingLlmQuestion ? (
            <div className="llm-labeling-empty">No questions yet.</div>
          ) : (
            llmQuestions.map((q) => {
              const key = String(q.id);
              const isActiveQuestion = key === String(activeLlmQuestionId);
              const isExpanded = expandedLlmQuestionIds.has(key);
              return (
                <div key={q.id} className="llm-labeling-question">
                  <TreeNode
                    label={q.name || `Question ${q.id}`}
                    customPaddingLeft={UNIT_TREE_NODE_PAD}
                    hasChildren
                    expanded={isExpanded}
                    active={isActiveQuestion && !activeLlmSection}
                    onClick={() =>
                      selectLlmQuestion(q.id, LLM_QUESTION_SECTION_IDS.info)
                    }
                    onToggle={() => toggleLlmQuestionExpanded(q.id)}
                  />
                  {isExpanded
                    ? LLM_QUESTION_SECTION_LABELS.map(({ key: sectionKey, label }) => (
                        <TreeNode
                          key={`${q.id}-${sectionKey}`}
                          label={label}
                          customPaddingLeft={UNIT_TREE_NODE_PAD + 18}
                          hasChildren={false}
                          active={isActiveQuestion && activeLlmSection === sectionKey}
                          onClick={() => selectLlmQuestion(q.id, sectionKey)}
                        />
                      ))
                    : null}
                </div>
              );
            })
          )}
        </div>
      ) : null}
      {!sidebarCollapsed && isComposerUserQuestionsRoute && course?.id ? (
        <div className="pd-sidebar-tree llm-labeling-section" role="tree">
          <div className="pd-sidebar-tree-program-root">
            <TreeNode
              label="User Questions"
              level={0}
              customPaddingLeft={PROGRAM_TREE_NODE_PAD}
              bold
              hasChildren={false}
              active={!activeUserQuestionId}
              onClick={() => selectUserQuestion(null)}
              trailingActions={
                <Tooltip title="Add question" placement="top" arrow enterDelay={400} disableInteractive>
                  <button
                    type="button"
                    className="pd-tree-node-action"
                    aria-label="Add question"
                    onClick={(e) => {
                      e.stopPropagation();
                      createUserQuestion();
                    }}
                  >
                    <AddIcon />
                  </button>
                </Tooltip>
              }
            />
          </div>
          {userQuestionsLoading && userQuestions.length === 0 ? (
            <div className="llm-labeling-empty">Loading...</div>
          ) : userQuestions.length === 0 ? (
            <div className="llm-labeling-empty">No questions yet.</div>
          ) : (
            userQuestions.map((field) => {
              const key = String(field.id);
              return (
                <div key={field.id} className="llm-labeling-question">
                  <TreeNode
                    label={field.name || `Question ${field.id}`}
                    activationBadge={
                      <NodeActivationBadge
                        activeByLanguage={field.active_by_language}
                        supportedLanguages={courseSupportedLanguages}
                      />
                    }
                    customPaddingLeft={UNIT_TREE_NODE_PAD}
                    hasChildren={false}
                    active={key === String(activeUserQuestionId)}
                    onClick={() => selectUserQuestion(field.id)}
                  />
                </div>
              );
            })
          )}
        </div>
      ) : null}
      {!sidebarCollapsed && !isComposerLlmQuestionsRoute && !isComposerUserQuestionsRoute ? (
      <DragDropContext onDragEnd={handleDragEnd}>
      <div className="pd-sidebar-tree" role="tree">
        {shouldShowSkillsSidebarTree ? (
          renderSkillsTree()
        ) : shouldShowProgramSidebarTree ? (
          <div key={course?.id ?? "program-tree"}>
            <div className="pd-sidebar-tree-program-root">
              <TreeNode
                label="Program"
                level={0}
                customPaddingLeft={PROGRAM_TREE_NODE_PAD}
                bold
                activationBadge={
                  course?.id != null ? (
                    <NodeActivationBadge
                      activeByLanguage={course.active_by_language}
                      supportedLanguages={courseSupportedLanguages}
                    />
                  ) : null
                }
                hasChildren={false}
                active={isProgramRootActive}
                onMouseEnter={course?.id != null ? () => prefetchTarget({ kind: "course", entityId: course.id }) : undefined}
                onFocus={course?.id != null ? () => prefetchTarget({ kind: "course", entityId: course.id }) : undefined}
                onClick={onProgramRootClick}
                trailingActions={
                  course?.id != null ? (
                    <>
                      <Tooltip
                        title="Import unit"
                        placement="top"
                        arrow
                        enterDelay={400}
                        disableInteractive
                      >
                        <span className="pd-tree-node-action-tooltip-wrap">
                          <button
                            type="button"
                            className="pd-tree-node-action pd-tree-node-action--import"
                            aria-label="Import unit"
                            disabled={importingCourseUnit}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestCourseUnitImport();
                            }}
                          >
                            <ImportIcon />
                          </button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Add unit" placement="top" arrow enterDelay={400} disableInteractive>
                        <button
                          type="button"
                          className="pd-tree-node-action"
                          aria-label="Add unit"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/composer/courses/edit-unit/new?courseId=${course.id}`);
                          }}
                        >
                          <AddIcon />
                        </button>
                      </Tooltip>
                    </>
                  ) : undefined
                }
              />
            </div>
            {units.length >= 2 && onRefreshCourse ? (
              <Droppable droppableId="course-units" type="COURSE_UNIT">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {units.map((unitItem, index) => (
                      <Draggable
                        key={unitItem.id}
                        draggableId={`unit-${unitItem.id}`}
                        index={index}
                      >
                        {(prov) => (
                          <div ref={prov.innerRef} {...prov.draggableProps}>
                            {renderUnitNode(unitItem, 0, prov.dragHandleProps)}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ) : (
              renderUnits(units, 0)
            )}
          </div>
        ) : null}
      </div>
      </DragDropContext>
      ) : null}
      {!sidebarCollapsed ? (
      <div
        className="pd-sidebar-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize course content sidebar"
        tabIndex={0}
        onMouseDown={onSidebarResizeMouseDown}
        onKeyDown={onSidebarResizeKeyDown}
      />
      ) : null}
      {importPortal}
      {courseUnitImportPortal}
      <input
        ref={lessonVersionStepImportInputRef}
        type="file"
        accept={BUNDLE_IMPORT_ACCEPT}
        onChange={onLessonVersionStepImportChange}
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
        disabled={importingLessonVersionStepId != null}
      />
      <input
        ref={lessonVersionStepImportFolderInputRef}
        type="file"
        webkitdirectory=""
        directory=""
        multiple
        onChange={onLessonVersionStepImportFolderChange}
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
        disabled={importingLessonVersionStepId != null}
      />
      <input
        ref={skillsImportInputRef}
        type="file"
        accept={BUNDLE_IMPORT_ACCEPT}
        onChange={handleSkillsSidebarImportFileChange}
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
        disabled={skillsImporting}
      />
      <input
        ref={skillsImportFolderInputRef}
        type="file"
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleSkillsSidebarImportFolderChange}
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
        disabled={skillsImporting}
      />
      <input
        ref={skillsImportAllInputRef}
        type="file"
        accept={BUNDLE_IMPORT_ACCEPT}
        onChange={handleSkillsSidebarImportAllFileChange}
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
        disabled={skillsImporting}
      />
      <input
        ref={skillsImportAllFolderInputRef}
        type="file"
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleSkillsSidebarImportAllFolderChange}
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
        disabled={skillsImporting}
      />
      <ManageLanguagesDialog
        open={manageLanguagesOpen}
        onClose={() => setManageLanguagesOpen(false)}
        course={course}
        onSaved={handleManageLanguagesSaved}
        showError={showError}
      />
      {ErrorPopupComponent}
    </aside>
  );
};

export default ContentEditorSidebar;
